import React from 'react';
import Icon from '../../../components/AppIcon';

const AttendeeStats = ({ attendees, filteredAttendees }) => {
  const totalAttendees = attendees?.length;
  const filteredCount = filteredAttendees?.length;
  
  const checkedInCount = filteredAttendees?.filter(a => a?.checkInStatus === 'checked-in')?.length;
  const notCheckedInCount = filteredAttendees?.filter(a => a?.checkInStatus === 'not-checked-in')?.length;
  const lateArrivals = filteredAttendees?.filter(a => a?.checkInStatus === 'late')?.length;
  
  const paidCount = filteredAttendees?.filter(a => a?.paymentStatus === 'paid')?.length;
  const pendingPayments = filteredAttendees?.filter(a => a?.paymentStatus === 'pending')?.length;
  const refundedCount = filteredAttendees?.filter(a => a?.paymentStatus === 'refunded')?.length;
  
  const totalRevenue = filteredAttendees?.filter(a => a?.paymentStatus === 'paid')?.reduce((sum, a) => sum + a?.amount, 0);
  
  const vipCount = filteredAttendees?.filter(a => a?.ticketTier === 'VIP')?.length;
  const premiumCount = filteredAttendees?.filter(a => a?.ticketTier === 'Premium')?.length;
  const standardCount = filteredAttendees?.filter(a => a?.ticketTier === 'Standard')?.length;
  
  const checkInRate = totalAttendees > 0 ? ((checkedInCount / totalAttendees) * 100)?.toFixed(1) : 0;

  const stats = [
    {
      title: 'Total Attendees',
      value: filteredCount?.toLocaleString(),
      subtitle: totalAttendees !== filteredCount ? `of ${totalAttendees?.toLocaleString()} total` : null,
      icon: 'Users',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      title: 'Checked In',
      value: checkedInCount?.toLocaleString(),
      subtitle: `${checkInRate}% check-in rate`,
      icon: 'UserCheck',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      title: 'Pending Check-in',
      value: notCheckedInCount?.toLocaleString(),
      subtitle: lateArrivals > 0 ? `${lateArrivals} late arrivals` : 'On time',
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      title: 'Revenue',
      value: `$${totalRevenue?.toLocaleString()}`,
      subtitle: `${paidCount} paid tickets`,
      icon: 'DollarSign',
      color: 'text-success',
      bgColor: 'bg-success/10'
    }
  ];

  const tierStats = [
    { tier: 'VIP', count: vipCount, color: 'bg-yellow-500' },
    { tier: 'Premium', count: premiumCount, color: 'bg-blue-500' },
    { tier: 'Standard', count: standardCount, color: 'bg-gray-500' }
  ];

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats?.map((stat, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{stat?.title}</p>
                <p className="text-2xl font-bold text-foreground mt-1">{stat?.value}</p>
                {stat?.subtitle && (
                  <p className="text-xs text-muted-foreground mt-1">{stat?.subtitle}</p>
                )}
              </div>
              <div className={`w-12 h-12 rounded-lg ${stat?.bgColor} flex items-center justify-center`}>
                <Icon name={stat?.icon} size={24} className={stat?.color} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Additional Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ticket Tier Breakdown */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-medium text-foreground mb-4 flex items-center">
            <Icon name="Tag" size={16} className="mr-2" />
            Ticket Tier Distribution
          </h3>
          <div className="space-y-3">
            {tierStats?.map((tier, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${tier?.color}`} />
                  <span className="text-sm font-medium text-foreground">{tier?.tier}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-foreground">{tier?.count}</span>
                  <span className="text-xs text-muted-foreground ml-1">
                    ({totalAttendees > 0 ? ((tier?.count / totalAttendees) * 100)?.toFixed(1) : 0}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Status */}
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-medium text-foreground mb-4 flex items-center">
            <Icon name="CreditCard" size={16} className="mr-2" />
            Payment Status
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={14} className="text-success" />
                <span className="text-sm font-medium text-foreground">Paid</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-foreground">{paidCount}</span>
                <span className="text-xs text-muted-foreground ml-1">
                  ({totalAttendees > 0 ? ((paidCount / totalAttendees) * 100)?.toFixed(1) : 0}%)
                </span>
              </div>
            </div>
            
            {pendingPayments > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={14} className="text-warning" />
                  <span className="text-sm font-medium text-foreground">Pending</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-foreground">{pendingPayments}</span>
                  <span className="text-xs text-muted-foreground ml-1">
                    ({totalAttendees > 0 ? ((pendingPayments / totalAttendees) * 100)?.toFixed(1) : 0}%)
                  </span>
                </div>
              </div>
            )}
            
            {refundedCount > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="RotateCcw" size={14} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Refunded</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-foreground">{refundedCount}</span>
                  <span className="text-xs text-muted-foreground ml-1">
                    ({totalAttendees > 0 ? ((refundedCount / totalAttendees) * 100)?.toFixed(1) : 0}%)
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Icon name="Zap" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Quick Stats</span>
          </div>
          <div className="flex items-center space-x-6 text-sm text-muted-foreground">
            <span>Last updated: {new Date()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            <span>•</span>
            <span>Real-time data</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendeeStats;